import React, { useEffect, useRef, useState } from 'react';
import { Download, Loader2, Pause, Volume2 } from 'lucide-react';

import { assistantsService, type Voix } from '../../services/api/assistants';

interface Props {
  texte: string;
  onTexteChange: (texte: string) => void;
  voix: string;
  onVoixChange: (voix: string) => void;
  /** Catalogue chargé une seule fois par le parent, pas par chaque réplique. */
  catalogue: Voix[];
  titre?: string;
  compact?: boolean;
}

// Même débit que le contrôle serveur : l'estimation affichée doit correspondre
// à celle qui fait accepter ou rejeter un spot. Valeur mesurée sur des pistes
// réellement produites, non estimée.
const CARACTERES_PAR_SECONDE = 17;
const LIMITE = 1200;

/**
 * Écoute et retouche d'une réplique.
 *
 * Un spot ne se juge pas à la lecture : il se juge à l'oreille. Le texte reste
 * donc modifiable, et chaque écoute porte sur la version affichée.
 *
 * Le texte et la voix sont tenus par le parent : c'est lui qui monte le spot
 * complet, et il doit livrer la version que l'utilisateur vient d'entendre.
 */
const AtelierVocal: React.FC<Props> = ({
  texte,
  onTexteChange,
  voix,
  onVoixChange,
  catalogue,
  titre,
  compact,
}) => {
  const [travail, setTravail] = useState(false);
  const [lecture, setLecture] = useState(false);
  const [erreur, setErreur] = useState('');
  const lecteur = useRef<HTMLAudioElement | null>(null);
  // La piste est gardée tant que le texte et la voix ne changent pas :
  // réécouter ne doit pas être refacturé.
  const piste = useRef<{ cle: string; url: string } | null>(null);

  useEffect(
    () => () => {
      lecteur.current?.pause();
      if (piste.current) URL.revokeObjectURL(piste.current.url);
    },
    [],
  );

  const secondes = Math.round(texte.trim().length / CARACTERES_PAR_SECONDE);
  const trop = texte.trim().length > LIMITE;

  const obtenirPiste = async () => {
    const cle = `${voix}|${texte}`;
    if (piste.current?.cle === cle) return piste.current.url;

    setTravail(true);
    try {
      const blob = await assistantsService.synthetiser(texte.trim(), voix || undefined, titre);
      if (piste.current) URL.revokeObjectURL(piste.current.url);
      piste.current = { cle, url: URL.createObjectURL(blob) };
      return piste.current.url;
    } finally {
      setTravail(false);
    }
  };

  const ecouter = async () => {
    if (lecture) {
      lecteur.current?.pause();
      setLecture(false);
      return;
    }
    setErreur('');
    try {
      const url = await obtenirPiste();
      const audio = new Audio(url);
      audio.onended = () => setLecture(false);
      lecteur.current = audio;
      await audio.play();
      setLecture(true);
    } catch (e: any) {
      setErreur(e.message || "La voix n'a pas pu être produite.");
    }
  };

  const telecharger = async () => {
    setErreur('');
    try {
      const url = await obtenirPiste();
      const lien = document.createElement('a');
      lien.href = url;
      lien.download = `${(titre || 'extrait sonore').replace(/[\\/:*?"<>|]/g, '')}.mp3`;
      document.body.appendChild(lien);
      lien.click();
      lien.remove();
    } catch (e: any) {
      setErreur(e.message || "Le fichier n'a pas pu être produit.");
    }
  };

  return (
    <div className="space-y-3">
      <textarea
        value={texte}
        onChange={(e) => onTexteChange(e.target.value)}
        rows={compact ? 3 : 6}
        placeholder="Texte à dire..."
        className="w-full resize-y rounded-md border border-[#DDE2DD] px-3 py-2 text-sm
          focus:border-eg-accent focus:outline-none focus:ring-1 focus:ring-eg-accent"
      />

      <div className="flex flex-wrap items-center gap-3">
        <select
          value={voix}
          onChange={(e) => onVoixChange(e.target.value)}
          className="rounded-md border border-[#DDE2DD] px-3 py-2 text-sm
            focus:border-eg-accent focus:outline-none"
        >
          <option value="">Voix par défaut</option>
          {catalogue.map((v) => (
            <option key={v.id} value={v.id}>
              {v.nom}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={ecouter}
          disabled={travail || !texte.trim() || trop}
          className="inline-flex items-center gap-2 rounded-md bg-eg-ombre px-4 py-2 text-sm
            font-medium text-eg-blanc transition-colors hover:bg-eg-encre disabled:opacity-50"
        >
          {travail ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : lecture ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Volume2 className="h-4 w-4" />
          )}
          {lecture ? 'Pause' : 'Écouter'}
        </button>

        <button
          type="button"
          onClick={telecharger}
          disabled={travail || !texte.trim() || trop}
          className="inline-flex items-center gap-2 rounded-md border border-[#DDE2DD] px-3 py-2
            text-sm text-eg-gris transition-colors hover:bg-eg-papier disabled:opacity-50"
          title="Télécharger cette réplique seule"
        >
          <Download className="h-4 w-4" />
        </button>

        <span className={`text-xs ${trop ? 'font-medium text-eg-accent' : 'text-eg-gris'}`}>
          {texte.trim().length} caractères · environ {secondes} s
          {trop && ` — au-delà de ${LIMITE}, découpez en répliques`}
        </span>
      </div>

      {erreur && (
        <p className="rounded-md bg-eg-accent/10 px-3 py-2 text-sm text-eg-accent">{erreur}</p>
      )}
    </div>
  );
};

export default AtelierVocal;
